import React, { useState, useMemo } from 'react';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Search, Plane, ArrowUp, ArrowDown, Minus, Crown, MapPinned } from 'lucide-react';

// Airline code to name mapping for better display
const AIRLINE_NAMES = {
    // US Airlines
    'AAL': 'American Airlines', 'AA': 'American Airlines',
    'UAL': 'United Airlines', 'UA': 'United Airlines',
    'DAL': 'Delta Air Lines', 'DL': 'Delta Air Lines',
    'SWA': 'Southwest Airlines', 'WN': 'Southwest Airlines',
    'JBU': 'JetBlue Airways', 'B6': 'JetBlue Airways',
    'ASA': 'Alaska Airlines', 'AS': 'Alaska Airlines',
    'NKS': 'Spirit Airlines', 'NK': 'Spirit Airlines',
    'FFT': 'Frontier Airlines', 'F9': 'Frontier Airlines',
    'HAL': 'Hawaiian Airlines', 'HA': 'Hawaiian Airlines',
    // European Airlines
    'BAW': 'British Airways', 'BA': 'British Airways',
    'DLH': 'Lufthansa', 'LH': 'Lufthansa',
    'AFR': 'Air France', 'AF': 'Air France',
    'KLM': 'KLM Royal Dutch', 'KL': 'KLM Royal Dutch',
    'EZY': 'easyJet', 'U2': 'easyJet',
    'RYR': 'Ryanair', 'FR': 'Ryanair',
    'VIR': 'Virgin Atlantic', 'VS': 'Virgin Atlantic',
    'IBE': 'Iberia', 'IB': 'Iberia',
    'SAS': 'SAS Scandinavian', 'SK': 'SAS Scandinavian',
    'AUA': 'Austrian Airlines', 'OS': 'Austrian Airlines',
    'SWR': 'Swiss', 'LX': 'Swiss',
    'TAP': 'TAP Portugal', 'TP': 'TAP Portugal',
    'AEE': 'Aegean Airlines', 'A3': 'Aegean Airlines',
    'FIN': 'Finnair', 'AY': 'Finnair',
    'AZA': 'ITA Airways', 'AZ': 'ITA Airways',
    'WZZ': 'Wizz Air', 'W6': 'Wizz Air',
    'VLG': 'Vueling', 'VY': 'Vueling',
    'EIN': 'Aer Lingus', 'EI': 'Aer Lingus',
    'THY': 'Turkish Airlines', 'TK': 'Turkish Airlines',
    'DY': 'Norwegian', 'NAX': 'Norwegian',
    // Middle East
    'UAE': 'Emirates', 'EK': 'Emirates',
    'ETD': 'Etihad Airways', 'EY': 'Etihad Airways',
    'QTR': 'Qatar Airways', 'QR': 'Qatar Airways',
    'SVA': 'Saudia', 'SV': 'Saudia',
    'GFA': 'Gulf Air', 'GF': 'Gulf Air',
    'MEA': 'Middle East Airlines', 'ME': 'Middle East Airlines',
    'RJA': 'Royal Jordanian', 'RJ': 'Royal Jordanian',
    // Asia-Pacific
    'SIA': 'Singapore Airlines', 'SQ': 'Singapore Airlines',
    'CPA': 'Cathay Pacific', 'CX': 'Cathay Pacific',
    'QFA': 'Qantas', 'QF': 'Qantas',
    'QJE': 'QantasLink', 
    'JST': 'Jetstar', 'JQ': 'Jetstar',
    'VOZ': 'Virgin Australia', 'VA': 'Virgin Australia',
    'ANA': 'All Nippon Airways', 'NH': 'All Nippon Airways',
    'JAL': 'Japan Airlines', 'JL': 'Japan Airlines',
    'KAL': 'Korean Air', 'KE': 'Korean Air',
    'AAR': 'Asiana Airlines', 'OZ': 'Asiana Airlines',
    'CAL': 'China Airlines', 'CI': 'China Airlines',
    'EVA': 'EVA Air', 'BR': 'EVA Air',
    'THA': 'Thai Airways', 'TG': 'Thai Airways',
    'MAS': 'Malaysia Airlines', 'MH': 'Malaysia Airlines',
    'GIA': 'Garuda Indonesia', 'GA': 'Garuda Indonesia',
    'ANZ': 'Air New Zealand', 'NZ': 'Air New Zealand',
    'CES': 'China Eastern', 'MU': 'China Eastern',
    'CSN': 'China Southern', 'CZ': 'China Southern',
    'CCA': 'Air China', 'CA': 'Air China',
    'HDA': 'Hong Kong Airlines', 'HX': 'Hong Kong Airlines',
    'PAL': 'Philippine Airlines', 'PR': 'Philippine Airlines',
    'VNA': 'Vietnam Airlines', 'VN': 'Vietnam Airlines',
    'AXM': 'AirAsia', 'AK': 'AirAsia',
    'SCO': 'Scoot', 'TR': 'Scoot',
    // Canada
    'ACA': 'Air Canada', 'AC': 'Air Canada',
    'WJA': 'WestJet', 'WS': 'WestJet',
    // South America
    'LAN': 'LATAM', 'LA': 'LATAM',
    'GLO': 'Gol', 'G3': 'Gol',
    'AVA': 'Avianca', 'AV': 'Avianca',
    'ARG': 'Aerolineas Argentinas', 'AR': 'Aerolineas Argentinas',
    'AMX': 'Aeromexico', 'AM': 'Aeromexico',
    // Africa
    'SAA': 'South African Airways', 'SA': 'South African Airways',
    'ETH': 'Ethiopian Airlines', 'ET': 'Ethiopian Airlines',
    'KQA': 'Kenya Airways', 'KQ': 'Kenya Airways',
    'RAM': 'Royal Air Maroc', 'AT': 'Royal Air Maroc',
    'MSR': 'EgyptAir', 'MS': 'EgyptAir',
};

// Australian airport codes
const AUSTRALIAN_AIRPORTS = ['SYD', 'MEL', 'BNE', 'PER', 'ADL', 'OOL', 'CNS', 'DRW', 'CBR', 'HBA', 'TSV', 'NTL', 'AVV', 'MCY', 'LST'];

// New Zealand airport codes
const NZ_AIRPORTS = ['AKL', 'WLG', 'CHC', 'ZQN', 'DUD', 'NPE', 'PMR', 'NSN', 'HLZ', 'ROT', 'NPL', 'TRG', 'IVC'];

// Japan airport codes
const JAPAN_AIRPORTS = ['NRT', 'HND', 'KIX', 'ITM', 'NGO', 'CTS', 'FUK', 'OKA', 'SDJ', 'HIJ'];

// UK airport codes
const UK_AIRPORTS = ['LHR', 'LGW', 'STN', 'LTN', 'MAN', 'BHX', 'EDI', 'GLA', 'BRS', 'NCL', 'LPL', 'BFS'];

// Germany airport codes
const GERMANY_AIRPORTS = ['FRA', 'MUC', 'DUS', 'TXL', 'BER', 'HAM', 'CGN', 'STR', 'HAJ', 'NUE'];

// France airport codes  
const FRANCE_AIRPORTS = ['CDG', 'ORY', 'NCE', 'LYS', 'MRS', 'TLS', 'BOD', 'NTE'];

// Hong Kong
const HK_AIRPORTS = ['HKG'];

// Korea airport codes
const KOREA_AIRPORTS = ['ICN', 'GMP', 'PUS', 'CJU', 'TAE'];

// Netherlands airport codes
const NETHERLANDS_AIRPORTS = ['AMS', 'EIN', 'RTM'];

// Codeshare partnership mapping - which US carrier partners with which operator
const CODESHARE_PARTNERSHIPS = {
    // American Airlines (AA/AAL) partnerships
    'AA': {
        'australia': 'Qantas',           // AA codeshares with Qantas in AU
        'new_zealand': 'Qantas',         // AA uses Qantas for NZ routes too
        'japan': 'Japan Airlines',       // AA partners with JAL
        'uk': 'British Airways',         // AA partners with BA (oneworld)
        'hong_kong': 'Cathay Pacific',   // AA partners with Cathay (oneworld)
        'default': 'Partner Airline'
    },
    'AAL': {
        'australia': 'Qantas',
        'new_zealand': 'Qantas', 
        'japan': 'Japan Airlines',
        'uk': 'British Airways',
        'hong_kong': 'Cathay Pacific',
        'default': 'Partner Airline'
    },
    // United Airlines (UA/UAL) partnerships
    'UA': {
        'australia': 'Virgin Australia', // UA partners with Virgin AU
        'new_zealand': 'Air New Zealand', // UA partners with Air NZ (Star Alliance)
        'japan': 'All Nippon Airways',   // UA partners with ANA (Star Alliance)
        'germany': 'Lufthansa',          // UA partners with Lufthansa (Star Alliance)
        'default': 'Partner Airline'
    },
    'UAL': {
        'australia': 'Virgin Australia',
        'new_zealand': 'Air New Zealand',
        'japan': 'All Nippon Airways',
        'germany': 'Lufthansa',
        'default': 'Partner Airline'
    },
    // Delta Airlines (DL/DAL) partnerships
    'DL': {
        'australia': 'Virgin Australia', // DL partners with Virgin AU
        'new_zealand': 'Virgin Australia',
        'korea': 'Korean Air',           // DL partners with Korean Air
        'france': 'Air France',          // DL partners with Air France (SkyTeam)
        'netherlands': 'KLM',            // DL partners with KLM (SkyTeam)
        'default': 'Partner Airline'
    },
    'DAL': {
        'australia': 'Virgin Australia',
        'new_zealand': 'Virgin Australia',
        'korea': 'Korean Air',
        'france': 'Air France',
        'netherlands': 'KLM',
        'default': 'Partner Airline'
    }
};

// Determine the region based on airport codes
const getRegionFromAirports = (departureIata, arrivalIata) => {
    const dep = departureIata?.toUpperCase();
    const arr = arrivalIata?.toUpperCase();
    
    // Check Australia
    if ((dep && AUSTRALIAN_AIRPORTS.includes(dep)) || (arr && AUSTRALIAN_AIRPORTS.includes(arr))) {
        // If both are AU or one is AU and other is NZ, it's Australia region
        if ((AUSTRALIAN_AIRPORTS.includes(dep) && AUSTRALIAN_AIRPORTS.includes(arr)) ||
            (AUSTRALIAN_AIRPORTS.includes(dep) && NZ_AIRPORTS.includes(arr)) ||
            (NZ_AIRPORTS.includes(dep) && AUSTRALIAN_AIRPORTS.includes(arr))) {
            return 'australia';
        }
        if (AUSTRALIAN_AIRPORTS.includes(dep) || AUSTRALIAN_AIRPORTS.includes(arr)) {
            return 'australia';
        }
    }
    
    // Check New Zealand
    if ((dep && NZ_AIRPORTS.includes(dep)) && (arr && NZ_AIRPORTS.includes(arr))) {
        return 'new_zealand';
    }
    if ((dep && NZ_AIRPORTS.includes(dep)) || (arr && NZ_AIRPORTS.includes(arr))) {
        return 'new_zealand';
    }
    
    // Check Japan
    if ((dep && JAPAN_AIRPORTS.includes(dep)) || (arr && JAPAN_AIRPORTS.includes(arr))) {
        return 'japan';
    }
    
    // Check UK
    if ((dep && UK_AIRPORTS.includes(dep)) || (arr && UK_AIRPORTS.includes(arr))) {
        return 'uk';
    }
    
    // Check Germany
    if ((dep && GERMANY_AIRPORTS.includes(dep)) || (arr && GERMANY_AIRPORTS.includes(arr))) {
        return 'germany';
    }
    
    // Check France
    if ((dep && FRANCE_AIRPORTS.includes(dep)) || (arr && FRANCE_AIRPORTS.includes(arr))) {
        return 'france';
    }
    
    // Check Hong Kong
    if ((dep && HK_AIRPORTS.includes(dep)) || (arr && HK_AIRPORTS.includes(arr))) {
        return 'hong_kong';
    }
    
    // Check Korea
    if ((dep && KOREA_AIRPORTS.includes(dep)) || (arr && KOREA_AIRPORTS.includes(arr))) {
        return 'korea';
    }
    
    // Check Netherlands
    if ((dep && NETHERLANDS_AIRPORTS.includes(dep)) || (arr && NETHERLANDS_AIRPORTS.includes(arr))) {
        return 'netherlands';
    }
    
    return null;
};

// Get the actual operating carrier for a codeshare flight
const getOperatingCarrier = (callsign, departureIata, arrivalIata) => {
    if (!callsign) return null;
    
    const cleaned = callsign.trim().toUpperCase();
    const prefix3 = cleaned.substring(0, 3);
    const prefix2 = cleaned.substring(0, 2);
    
    // Check if this is a US carrier codeshare
    const carrierCode = CODESHARE_PARTNERSHIPS[prefix3] ? prefix3 : 
                        CODESHARE_PARTNERSHIPS[prefix2] ? prefix2 : null;
    
    if (!carrierCode) return null;
    
    // Determine the region
    const region = getRegionFromAirports(departureIata, arrivalIata);
    
    if (!region) return null;
    
    // Get the operating carrier for this region
    const partnerships = CODESHARE_PARTNERSHIPS[carrierCode];
    return partnerships[region] || partnerships['default'];
};

// Check if it's a codeshare (US airline on route in partner region)
const isCodeshare = (callsign, originCountry, airlineName, departureIata, arrivalIata) => {
    // US airlines that commonly codeshare
    const usAirlines = ['American Airlines', 'United Airlines', 'Delta Air Lines', 'Delta'];
    const usCallsignPrefixes = ['AAL', 'AA', 'UAL', 'UA', 'DAL', 'DL'];
    
    // Check if it's a US airline (by name or callsign)
    let isUSAirline = false;
    let carrierPrefix = null;
    
    if (airlineName && usAirlines.some(us => airlineName.includes(us))) {
        isUSAirline = true;
    }
    if (callsign) {
        const cleaned = callsign.trim().toUpperCase();
        const prefix3 = cleaned.substring(0, 3);
        const prefix2 = cleaned.substring(0, 2);
        if (usCallsignPrefixes.includes(prefix3)) {
            isUSAirline = true;
            carrierPrefix = prefix3;
        } else if (usCallsignPrefixes.includes(prefix2)) {
            isUSAirline = true;
            carrierPrefix = prefix2;
        }
    }
    
    if (!isUSAirline) return { isCodeshare: false, operatingCarrier: null };
    
    // Get the operating carrier based on route
    const operatingCarrier = getOperatingCarrier(callsign, departureIata, arrivalIata);
    
    if (operatingCarrier) {
        return { isCodeshare: true, operatingCarrier };
    }
    
    // Fallback: check by origin country
    const nonUSCountries = ['Australia', 'New Zealand', 'Japan', 'China', 'Germany', 'France', 'United Kingdom', 'Brazil', 'India', 'South Korea', 'Hong Kong', 'Netherlands'];
    if (nonUSCountries.includes(originCountry)) {
        return { isCodeshare: true, operatingCarrier: 'Partner Airline' };
    }
    
    return { isCodeshare: false, operatingCarrier: null };
};

// Parse callsign to extract airline name - handle codeshares with smart mapping
const getAirlineName = (callsign, originCountry, existingAirlineName, departureIata, arrivalIata) => {
    // First check if it's a codeshare
    const codeshareInfo = isCodeshare(callsign, originCountry, existingAirlineName, departureIata, arrivalIata);
    
    if (codeshareInfo.isCodeshare && codeshareInfo.operatingCarrier) {
        // Return the actual operating carrier name
        return `${codeshareInfo.operatingCarrier}`;
    }
    
    // If there's an existing airline name from the API and not a codeshare, use it
    if (existingAirlineName) {
        return existingAirlineName;
    }
    
    if (!callsign) return null;
    const cleaned = callsign.trim().toUpperCase();
    
    // Try 3-letter ICAO code first
    const prefix3 = cleaned.substring(0, 3);
    if (AIRLINE_NAMES[prefix3]) {
        return AIRLINE_NAMES[prefix3];
    }
    
    // Try 2-letter IATA code
    const prefix2 = cleaned.substring(0, 2);
    if (AIRLINE_NAMES[prefix2]) {
        return AIRLINE_NAMES[prefix2];
    }
    
    return null;
};

export const FlightList = ({ 
    flights = [], 
    selectedFlight, 
    onFlightSelect,
    isLoading = false,
    isPremiumData = false,
    searchFilter = '',
    onSearchChange = null
}) => {
    // Use lifted state from parent if provided, otherwise use local state
    const [localSearchQuery, setLocalSearchQuery] = useState('');
    const searchQuery = onSearchChange ? searchFilter : localSearchQuery;
    const setSearchQuery = onSearchChange ? onSearchChange : setLocalSearchQuery;

    // Add airline names to flights for search - handle codeshares
    const flightsWithAirlines = useMemo(() => {
        return flights.map(flight => ({
            ...flight,
            airlineName: getAirlineName(
                flight.callsign, 
                flight.origin_country, 
                flight.airline_name,
                flight.departure_iata,
                flight.arrival_iata
            )
        }));
    }, [flights]);

    // Filter flights based on search (now includes airline names)
    const filteredFlights = useMemo(() => {
        if (!searchQuery.trim()) return flightsWithAirlines;
        
        const query = searchQuery.toLowerCase();
        return flightsWithAirlines.filter(flight => 
            (flight.callsign?.toLowerCase() || '').includes(query) ||
            (flight.icao24?.toLowerCase() || '').includes(query) ||
            (flight.origin_country?.toLowerCase() || '').includes(query) ||
            (flight.airlineName?.toLowerCase() || '').includes(query) ||
            (flight.departure_iata?.toLowerCase() || '').includes(query) ||
            (flight.arrival_iata?.toLowerCase() || '').includes(query)
        );
    }, [flightsWithAirlines, searchQuery]);

    // Sort flights by callsign
    const sortedFlights = useMemo(() => {
        return [...filteredFlights].sort((a, b) => {
            const callA = a.callsign || a.icao24 || '';
            const callB = b.callsign || b.icao24 || '';
            return callA.localeCompare(callB);
        });
    }, [filteredFlights]);

    const getVerticalIcon = (rate) => {
        if (!rate) return <Minus className="h-3 w-3 text-[#64748b]" />;
        if (rate > 1) return <ArrowUp className="h-3 w-3 text-green-500" />;
        if (rate < -1) return <ArrowDown className="h-3 w-3 text-yellow-500" />;
        return <Minus className="h-3 w-3 text-[#64748b]" />;
    };

    return (
        <div className="h-full flex flex-col bg-[#1e293b]/50 backdrop-blur-md rounded-xl border border-[#334155]">
            {/* Header */}
            <div className="p-4 border-b border-[#334155]">
                <h2 className="text-lg font-bold text-white mb-3">Active Flights</h2>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#64748b]" />
                    <Input
                        type="text"
                        placeholder="Search by callsign or country..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-[#071D35] border-[#334155] text-white placeholder:text-[#64748b] focus:border-[#f07050]"
                        data-testid="flight-search-input"
                    />
                </div>
            </div>

            {/* Flight List */}
            <ScrollArea className="flex-1">
                <div className="p-2">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-8 text-[#64748b]">
                            <div className="animate-spin mb-3">
                                <Plane className="h-8 w-8" />
                            </div>
                            <p className="text-sm">Loading flights...</p>
                        </div>
                    ) : sortedFlights.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-[#64748b]">
                            <Plane className="h-8 w-8 mb-3" />
                            <p className="text-sm">
                                {searchQuery ? 'No flights match your search' : 'No flights available'}
                            </p>
                        </div>
                    ) : (
                        sortedFlights.map((flight) => (
                            <div
                                key={flight.icao24}
                                onClick={() => onFlightSelect(flight)}
                                className={`flight-list-item p-3 rounded-lg cursor-pointer mb-1 ${
                                    selectedFlight?.icao24 === flight.icao24
                                        ? 'bg-[#f07050]/10 border-l-2 border-[#f07050]'
                                        : 'hover:bg-white/5 border-l-2 border-transparent'
                                }`}
                                data-testid={`flight-list-item-${flight.icao24}`}
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2">
                                        <Plane 
                                            className={`h-4 w-4 ${
                                                selectedFlight?.icao24 === flight.icao24
                                                    ? 'text-[#f07050]'
                                                    : 'text-white'
                                            }`}
                                            style={{ transform: `rotate(${(flight.heading || 0) - 90}deg)` }}
                                        />
                                        <span className="font-mono font-bold text-white">
                                            {flight.callsign || flight.icao24}
                                        </span>
                                        {flight.is_premium_data && (
                                            <Crown className="h-3 w-3 text-[#f07050]" title="Premium Data" />
                                        )}
                                    </div>
                                    {getVerticalIcon(flight.vertical_rate)}
                                </div>
                                <div className="flex items-center justify-between text-xs text-[#94a3b8]">
                                    <span className="truncate max-w-[120px]">
                                        {flight.airlineName || flight.origin_country || 'Unknown'}
                                    </span>
                                    <div className="flex items-center gap-3 font-mono">
                                        <span>{flight.altitude_feet?.toFixed(0) || '---'} ft</span>
                                        <span>{flight.velocity_knots?.toFixed(0) || '---'} kts</span>
                                    </div>
                                </div>
                                {/* Show route for premium data */}
                                {flight.departure_iata && flight.arrival_iata && (
                                    <div className="flex items-center gap-1 mt-1 text-xs">
                                        <MapPinned className="h-3 w-3 text-[#f07050]" />
                                        <span className="text-[#f07050] font-mono">
                                            {flight.departure_iata} → {flight.arrival_iata}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </ScrollArea>

            {/* Footer */}
            <div className="p-3 border-t border-[#334155] text-center">
                <span className="text-xs text-[#64748b] font-mono">
                    Showing {sortedFlights.length} of {flights.length} flights
                </span>
            </div>
        </div>
    );
};
